/**
 * TipTap Email Button Extension
 *
 * Inserts an email-safe CTA button into the editor.
 * Renders as a styled <a> tag wrapped in a centered container.
 * Output HTML uses inline styles for maximum email client compatibility.
 */
import { Node, mergeAttributes } from '@tiptap/core';

const EmailButton = Node.create({
    name: 'emailButton',

    group: 'block',

    atom: true, // non-editable block — treated as a single unit

    addAttributes() {
        return {
            text: { default: 'Click Here' },
            href: { default: '#' },
            backgroundColor: { default: '#1c1917' },
            textColor: { default: '#ffffff' },
            borderRadius: { default: '6' },
            alignment: { default: 'center' },
            width: { default: 'auto' }, // 'auto' or 'full'
            fontSize: { default: '16' },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-email-button]',
                getAttrs: (node) => {
                    const link = node.querySelector('a');
                    if (!link) return false;
                    return {
                        text: link.textContent || 'Click Here',
                        href: link.getAttribute('href') || '#',
                        backgroundColor: link.getAttribute('data-bg') || '#1c1917',
                        textColor: link.getAttribute('data-tc') || '#ffffff',
                        borderRadius: link.getAttribute('data-br') || '6',
                        alignment: node.getAttribute('data-align') || 'center',
                        width: link.getAttribute('data-width') || 'auto',
                        fontSize: link.getAttribute('data-fs') || '16',
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const {
            text, href, backgroundColor, textColor,
            borderRadius, alignment, width, fontSize,
        } = HTMLAttributes;

        const btnWidth = width === 'full' ? 'display:block;width:100%;' : 'display:inline-block;';
        const align = alignment === 'left' ? 'left' : alignment === 'right' ? 'right' : 'center';

        // Email-safe inline-styled button
        return [
            'div',
            mergeAttributes({
                'data-email-button': 'true',
                'data-align': align,
                style: `text-align:${align};margin:16px 0;`,
            }),
            [
                'a',
                {
                    href,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    'data-bg': backgroundColor,
                    'data-tc': textColor,
                    'data-br': borderRadius,
                    'data-width': width,
                    'data-fs': fontSize,
                    style: `${btnWidth}padding:12px 28px;background-color:${backgroundColor};color:${textColor};border-radius:${borderRadius}px;text-decoration:none;font-weight:600;font-size:${fontSize}px;font-family:Arial,Helvetica,sans-serif;line-height:1.4;mso-padding-alt:0;text-align:center;`,
                },
                text,
            ],
        ];
    },

    addCommands() {
        return {
            insertEmailButton: (attrs = {}) => ({ chain }) => {
                return chain()
                    .insertContent({
                        type: this.name,
                        attrs: {
                            text: attrs.text || 'Click Here',
                            href: attrs.href || '#',
                            backgroundColor: attrs.backgroundColor || '#1c1917',
                            textColor: attrs.textColor || '#ffffff',
                            borderRadius: attrs.borderRadius || '6',
                            alignment: attrs.alignment || 'center',
                            width: attrs.width || 'auto',
                            fontSize: attrs.fontSize || '16',
                        },
                    })
                    .run();
            },

            updateEmailButton: (attrs) => ({ tr, state, dispatch }) => {
                const { selection } = state;
                const node = state.doc.nodeAt(selection.from);
                if (node?.type.name !== 'emailButton') return false;

                if (dispatch) {
                    const newAttrs = { ...node.attrs, ...attrs };
                    tr.setNodeMarkup(selection.from, undefined, newAttrs);
                    dispatch(tr);
                }
                return true;
            },
        };
    },
});

export default EmailButton;
